<template>
  <Card :is-loading="isLoading">
    <div class="curated-deal-tile">
      <div class="curated-deal-tile__header">
        <div class="curated-deal-tile__header__left">
          <h3 class="curated-deal-tile__header__left__title">
            {{ deal.name }}<br />
            <span class="curated-deal-tile__header__left__dates"
              >{{ deal.startDateDescription }} – {{ deal.endDateDescription }}
            </span>
          </h3>
          <div style="display: flex; flex-direction: column; justify-content: flex-end; align-items: flex-end">
            <div class="badge badge-sm badge-disabled-hollow monospace font-weight-normal">
              {{ deal.dealIDFormatted }}
            </div>
            <div
              class="mt-2 badge badge-sm font-weight-normal monospace font-weight-normal"
              :class="{
                'badge-disabled': deal.state === 'D',
                'badge-success': deal.hasStarted && deal.state === 'P',
                'badge-info': deal.state === 'P' && (!deal.hasStarted || deal.hasEnded),
              }">
              {{ capitalize(deal.status) }}
            </div>
          </div>
        </div>
      </div>
      <div class="curated-deal-tile__body">
        <div class="curated-deal-tile__body__kpis mb-3">
          <kpi
            :label="$t('names.impressionDelivered', { n: 2 })"
            :value="deal.totalImpressionsFormatted"
            :is-money="false"
            :is-loading="deal.deliveryStats == null"
            icon="icon-colored-impression" />
          <kpi
            :label="$t('names.averageCPM')"
            :value="deal.averageCPMFormatted"
            :is-money="false"
            :is-loading="deal.deliveryStats == null"
            icon="icon-colored-cpm" />
          <kpi
            :label="$t('names.revenue')"
            :value="deal.totalSpendFormatted"
            :is-money="false"
            :is-loading="deal.deliveryStats == null"
            icon="icon-colored-budget" />
        </div>

        <!-- VENUE TYPES AND MARKETS -->
        <template
          v-for="category in categories"
          :key="category.title">
          <template v-if="category.included.length > 0 || category.excluded.length > 0">
            <div class="curated-deal-tile__body__section-title">
              {{ category.title }}
              <span class="ml-2 badge badge-rounded badge-sm badge-disabled">{{
                category.included.length + category.excluded.length
              }}</span>
            </div>
            <ul class="curated-deal-tile__body__media-types">
              <li
                v-for="s in category.included"
                :key="s.id">
                <span class="badge badge-xs badge-success-dark-hollow">{{ s.name }}</span>
              </li>
              <li
                v-for="s in category.excluded"
                :key="s.id">
                <span class="badge badge-xs badge-danger-hollow">{{ s.name }}</span>
              </li>
            </ul>
          </template>
        </template>

        <template v-if="props.suppliers != null && props.suppliers.length > 0 && uniqueSuppliers.length > 0">
          <div class="curated-deal-tile__body__section-title">
            {{ capitalize($t('names.mediaOwner', { n: 2 })) }}
            <span class="ml-2 badge badge-rounded badge-sm badge-disabled">{{ uniqueSuppliers.length }}</span>
          </div>
          <ul class="curated-deal-tile__body__suppliers">
            <li
              v-for="s in uniqueSuppliers"
              :key="s.id">
              <img
                v-if="s.logoUrl"
                :src="s.logoUrl"
                :alt="s.name" />
              <span v-else>{{ s.name }}</span>
            </li>
          </ul>
        </template>

        <div style="flex-grow: 1"></div>

        <div class="curated-deal-tile__actions">
          <button
            class="btn btn-secondary-hollow btn-xs"
            @click="router.push(deal.endpoint)">
            {{ $t('messages.seeDetails') }}
          </button>
        </div>
      </div>
      <div
        v-if="deal.co2EmissionsScore"
        class="curated-deal-tile-green-score">
        <GreenScore
          :co2-score="deal.co2EmissionsScore"
          :is-animating="true"
          :show-green-leaf="deal.co2EmissionsScore >= DEFAULT_SUSTAINABILITY_THRESHOLD"
          width="5rem"
          height="5rem" />
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
  //#region Imports

  import { capitalize, computed } from 'vue';

  import Card from '@/common/components/controls/Card.vue';
  import GreenScore from '@/common/components/controls/GreenScore.vue';
  import { Demographic, Market, MediaType } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import OrganizationDSP from '@/common/models/misc-api-models/OrganizationDSP';
  import Package, { DEFAULT_SUSTAINABILITY_THRESHOLD } from '@/common/models/package/Package';
  import Kpi from '@/org-app/components/controls/KPI.vue';
  import router from '@/router';
  import i18n from '@/vueI18n';

  //#endregion

  //#region Props

  interface ICuratedDealTileProps {
    deal: Package;
    demographics: Demographic[];
    isLoading: boolean;
    locations?: Location[];
    markets?: Market[];
    mediaTypes?: MediaType[];
    suppliers?: MediaOwner[];
    buyerOrganizationDSPs: OrganizationDSP[];
  }

  const props = defineProps<ICuratedDealTileProps>();

  //#endregion

  //#region Data

  //#endregion

  //#region Computed

  const mediaTypesFlattened = computed(() => {
    const getChildren = (mt) => [mt, ...mt.children.map((x) => getChildren(x)).flat()];
    return props.mediaTypes?.map((mt) => getChildren(mt)).flat() || [];
  });

  const uniqueSuppliers = computed(() => {
    const ids = Array.from(new Set(props.deal.packages.map((x) => x.mediaOwnerIDs).flat()));
    return ids.map((x) => props.suppliers.find((s) => x === s.id)).filter((x) => x != null);
  });

  const uniqueMediaTypes = computed(() => {
    const ids = [
      ...(props.deal.mediaTypeIDs || []),
      ...Array.from(new Set(props.deal.packages.map((x) => x.mediaTypeIDs).flat())),
    ];
    return ids.map((x) => mediaTypesFlattened.value.find((s) => x === s.id));
  });

  const uniqueExcludedMediaTypes = computed(() => {
    const ids = [
      ...(props.deal.mediaTypeExclusionIDs || []),
      ...Array.from(new Set(props.deal.packages.map((x) => x.mediaTypeExclusionIDs).flat())),
    ];
    return ids.map((x) => mediaTypesFlattened.value.find((s) => x === s.id));
  });

  const uniqueMarkets = computed(() => {
    const ids = [
      ...(props.deal.marketIDs || []),
      ...Array.from(new Set(props.deal.packages.map((x) => x.marketIDs).flat())),
    ];
    return ids.map((x) => props.markets?.find((s) => x === s.id)).filter((x) => x != null);
  });

  const uniqueExcludedMarkets = computed(() => {
    const ids = [
      ...(props.deal.marketExclusionIDs || []),
      ...Array.from(new Set(props.deal.packages.map((x) => x.marketExclusionIDs).flat())),
    ];
    return ids.map((x) => props.markets?.find((s) => x === s.id)).filter((x) => x != null);
  });

  const uniqueLocations = computed(() => {
    const ids = [
      ...(props.deal.locationIDs || []),
      ...Array.from(new Set(props.deal.packages.map((x) => x.locationIDs).flat())),
    ];
    return ids.map((x) => props.locations?.find((s) => x === s.id)).filter((x) => x != null);
  });

  const uniqueExcludedLocations = computed(() => {
    const ids = [
      ...(props.deal.locationExclusionIDs || []),
      ...Array.from(new Set(props.deal.packages.map((x) => x.locationExclusionIDs).flat())),
    ];
    return ids.map((x) => props.locations?.find((s) => x === s.id)).filter((x) => x != null);
  });

  const categories = computed(() => {
    return [
      ...(props.mediaTypes
        ? [
            {
              title: capitalize(i18n.global.t('names.venueType', { n: 2 })),
              included: uniqueMediaTypes.value,
              excluded: uniqueExcludedMediaTypes.value,
            },
          ]
        : []),
      ...(props.markets
        ? [
            {
              title: capitalize(i18n.global.t('names.market', { n: 2 })),
              included: uniqueMarkets.value,
              excluded: uniqueExcludedMarkets.value,
            },
          ]
        : []),
      ...(props.locations
        ? [
            {
              title: capitalize(i18n.global.t('names.location', { n: 2 })),
              included: uniqueLocations.value,
              excluded: uniqueExcludedLocations.value,
            },
          ]
        : []),
    ];
  });

  //#endregion

  //#region Functions

  //#endregion

  //#region Lifecycle hooks

  //#endregion

  //#region Initialization

  //#endregion
</script>

<style lang="scss" scoped>
  @use 'sass:color';
  @use '@/styles/variables.scss' as *;

  .curated-deal-tile {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;

    .curated-deal-tile__header {
      padding-bottom: 0.8rem;
      //border-bottom: dotted 2px $borders;

      .curated-deal-tile__header__left {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .curated-deal-tile__header__left__title {
          font-weight: bold;
          overflow: hidden;
          text-overflow: ellipsis;

          .curated-deal-tile__header__left__dates {
            font-weight: 300;
            font-size: 0.7em;
            color: var(--textMutedColor);
          }
        }
      }
    }

    .curated-deal-tile__body {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-end;

      .curated-deal-tile__body__section-title {
        margin-top: 1.6rem;
        text-transform: uppercase;
        color: var(--textMutedColor);
        border-bottom: dotted 2px $borders;
        padding-bottom: 0.4rem;
        font-size: 0.8em;
        font-weight: 600;
      }

      .curated-deal-tile__body__suppliers,
      .curated-deal-tile__body__media-types {
        padding: 0;
        margin: 1rem 0 0 0;
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        li {
          list-style: none;
          margin-right: 0.6rem;
          margin-bottom: 0.3rem;

          &:last-of-type {
            margin-right: 0;
          }
        }

        img {
          max-width: 10rem;
          max-height: 3rem;
        }
      }

      .curated-deal-tile__body__kpis {
        display: grid;
        grid-gap: 1rem;
        grid-template-columns: 1fr 1fr 1fr;
        border: solid 1px $borders-light;
        background: $almost-white;
        border-radius: 0.8rem;
      }
    }

    .curated-deal-tile__actions {
      display: flex;
      justify-content: flex-end;
    }

    .curated-deal-tile-green-score {
      position: absolute;
      top: -4rem;
      right: -5rem;
    }
  }
</style>
